/**
 * Mantiene el carrito coherente con las reglas de cintas (product-type "Cinta").
 * Requiere type="module" para actualizar el store del drawer vía assignCart.
 */
import { a as assignCart, b as setCartDisplay } from './__vite-cart.fb14b6a6.js';

var MEDAL_TAGS = ['Medallas Rústicas', 'Medallas Religiosas'];
var CINTA_TYPE = 'cinta';
var TAG_CINTA_INCLUIDA = 'cinta-incluida';
var TAG_CINTA_EXTRA = 'cinta-incluida-extra';
var LINK_PROPERTIES = ['Acompaña a', 'Relacionado con'];

var syncing = false;
var syncQueued = false;

function normalizeTag(tag) {
  return String(tag || '').trim().toLowerCase();
}

function itemTags(item) {
  return (item.product_tags || []).map(normalizeTag);
}

function hasTag(item, tag) {
  return itemTags(item).indexOf(normalizeTag(tag)) !== -1;
}

function isCinta(item) {
  return normalizeTag(item.product_type) === CINTA_TYPE;
}

function isAnchor(item) {
  if (isCinta(item)) return false;
  return MEDAL_TAGS.some(function (tag) {
    return hasTag(item, tag);
  });
}

function getLinkedTitle(item) {
  var props = item.properties || {};
  for (var i = 0; i < LINK_PROPERTIES.length; i++) {
    var value = props[LINK_PROPERTIES[i]];
    if (value && String(value).trim()) {
      return String(value).trim();
    }
  }
  return null;
}

function isCintaIncluida(item) {
  return hasTag(item, TAG_CINTA_INCLUIDA);
}

function isCintaExtra(item) {
  return hasTag(item, TAG_CINTA_EXTRA);
}

function getEffectiveQty(item, updates) {
  if (updates[item.key] !== undefined) {
    return updates[item.key];
  }
  return item.quantity || 1;
}

function countQtyByTitle(items, titleKey) {
  var counts = Object.create(null);
  items.forEach(function (item) {
    var title = item[titleKey];
    if (!title) return;
    counts[title] = (counts[title] || 0) + (item.quantity || 1);
  });
  return counts;
}

function sumQtyByLinkedTitle(items, updates) {
  var counts = Object.create(null);
  items.forEach(function (item) {
    if (getEffectiveQty(item, updates) <= 0) return;
    var linkTitle = getLinkedTitle(item);
    if (!linkTitle) return;
    counts[linkTitle] =
      (counts[linkTitle] || 0) + getEffectiveQty(item, updates);
  });
  return counts;
}

function markExcessQty(items, allowedByTitle, updates) {
  var keptByTitle = Object.create(null);

  items.forEach(function (item) {
    var currentQty = getEffectiveQty(item, updates);
    if (currentQty <= 0) {
      updates[item.key] = 0;
      return;
    }

    var linkTitle = getLinkedTitle(item);
    if (!linkTitle) {
      updates[item.key] = 0;
      return;
    }

    var allowed = allowedByTitle[linkTitle] || 0;
    var kept = keptByTitle[linkTitle] || 0;

    if (kept >= allowed) {
      updates[item.key] = 0;
      return;
    }

    if (kept + currentQty > allowed) {
      updates[item.key] = allowed - kept;
      keptByTitle[linkTitle] = allowed;
      return;
    }

    keptByTitle[linkTitle] = kept + currentQty;
  });
}

function getCartUpdates(items) {
  var updates = Object.create(null);
  var anchors = items.filter(isAnchor);
  var cintas = items.filter(isCinta);

  if (!cintas.length) {
    return updates;
  }

  if (!anchors.length) {
    cintas.forEach(function (item) {
      updates[item.key] = 0;
    });
  } else {

  var anchorQtyByTitle = countQtyByTitle(anchors, 'product_title');
  var anchorTitles = new Set(Object.keys(anchorQtyByTitle));

  cintas.forEach(function (item) {
    var linkTitle = getLinkedTitle(item);
    if (!linkTitle || !anchorTitles.has(linkTitle)) {
      updates[item.key] = 0;
    }
  });

  var standardCintas = cintas.filter(function (item) {
    return !isCintaExtra(item) && updates[item.key] !== 0;
  });
  var extras = cintas.filter(function (item) {
    return isCintaExtra(item) && updates[item.key] !== 0;
  });

  markExcessQty(standardCintas, anchorQtyByTitle, updates);

  var incluidas = cintas.filter(function (item) {
    return isCintaIncluida(item) && getEffectiveQty(item, updates) > 0;
  });
  var incluidaQtyByTitle = sumQtyByLinkedTitle(incluidas, updates);
  markExcessQty(extras, incluidaQtyByTitle, updates);

  var totalIncluidaQty = 0;
  incluidas.forEach(function (item) {
    totalIncluidaQty += getEffectiveQty(item, updates);
  });

  var totalExtraQty = 0;
  extras.forEach(function (item) {
    var currentQty = getEffectiveQty(item, updates);
    if (currentQty <= 0) {
      return;
    }

    if (totalExtraQty + currentQty > totalIncluidaQty) {
      var allowedExtra = Math.max(0, totalIncluidaQty - totalExtraQty);
      updates[item.key] = allowedExtra;
      totalExtraQty = totalIncluidaQty;
      return;
    }

    totalExtraQty += currentQty;
  });
  }

  var result = Object.create(null);
  cintas.forEach(function (item) {
    if (updates[item.key] === undefined) {
      return;
    }

    var newQty = updates[item.key];
    var oldQty = item.quantity || 1;
    if (newQty !== oldQty) {
      result[item.key] = newQty;
    }
  });

  return result;
}

function parseSectionJson(sectionHtml) {
  if (!sectionHtml) {
    return { items: [] };
  }

  var text = String(sectionHtml).trim();
  var start = text.indexOf('{');
  var end = text.lastIndexOf('}');

  if (start === -1 || end === -1 || end < start) {
    return { items: [] };
  }

  return JSON.parse(text.slice(start, end + 1));
}

async function enrichCartItems(cart) {
  if (!cart || !cart.items || !cart.items.length) {
    return cart;
  }

  var routes = window.themeVariables && window.themeVariables.routes;
  if (!routes || !routes.cartUrl) {
    return cart;
  }

  var sectionResponse = await fetch(routes.cartUrl + '?sections=cart-complement-json', {
    cache: 'reload',
  });
  var sections = await sectionResponse.json();
  var complement = parseSectionJson(sections['cart-complement-json']);
  var complementItems = complement.items || [];

  cart.items = cart.items.map(function (item, index) {
    var extra = complementItems[index] || {};
    return Object.assign({}, item, {
      product_type: extra.product_type || item.product_type || '',
      product_tags: extra.product_tags || [],
    });
  });

  return cart;
}

async function fetchEnrichedCart() {
  var routes = window.themeVariables && window.themeVariables.routes;
  if (!routes || !routes.cartUrl) {
    return null;
  }

  var cartResponse = await fetch(routes.cartUrl + '.js', { cache: 'reload' });
  var cart = await cartResponse.json();

  return enrichCartItems(cart);
}

async function applyCartUpdates(lineUpdates) {
  var routes = window.themeVariables && window.themeVariables.routes;
  var keys = Object.keys(lineUpdates || {});
  if (!routes || !routes.cartUrl || !keys.length) {
    return null;
  }

  var response = await fetch(routes.cartUrl + '/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ updates: lineUpdates }),
  });

  if (!response.ok) {
    throw new Error('[cart-ribbon-sync] cart update failed: ' + response.status);
  }

  return response.json();
}

function getMiniCartDrawer() {
  return document.getElementById('mini-cart');
}

async function fetchMiniCartSectionHtml() {
  var routes = window.themeVariables && window.themeVariables.routes;
  if (!routes || !routes.cartUrl) {
    return null;
  }

  var drawer = getMiniCartDrawer();
  var sectionKey =
    (drawer && drawer.getAttribute('section')) || 'mini-cart';

  var response = await fetch(
    routes.cartUrl + '?sections=' + encodeURIComponent(sectionKey),
    { cache: 'reload' }
  );
  var sections = await response.json();

  return sections[sectionKey] || sections['mini-cart'] || null;
}

function applyMiniCartHtml(html) {
  var drawer = getMiniCartDrawer();
  if (!drawer || !html) {
    return false;
  }

  var target = drawer.querySelector('[cart-loader-target]');
  if (!target) {
    return false;
  }

  var wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  var root = wrapper.firstElementChild || wrapper;
  var scrollEl = drawer.querySelector('.drawer__content');
  var scrollTop = scrollEl ? scrollEl.scrollTop : 0;

  target.innerHTML = '';
  Array.from(root.children).forEach(function (child) {
    target.appendChild(child);
  });

  if (scrollEl) {
    scrollEl.scrollTop = scrollTop;
  }

  var spinner = drawer.querySelector('[cart-loader-spinner]');
  if (spinner) {
    spinner.style.display = 'none';
  }

  drawer.isRendered = true;
  setCartDisplay('mini-cart');

  return true;
}

async function forceMiniCartRerender() {
  var html = await fetchMiniCartSectionHtml();
  return applyMiniCartHtml(html);
}

var drawerRefreshTimer = null;

function scheduleDrawerRefresh(delay) {
  window.clearTimeout(drawerRefreshTimer);
  drawerRefreshTimer = window.setTimeout(function () {
    forceMiniCartRerender().catch(function (error) {
      console.error('[cart-ribbon-sync] drawer refresh', error);
    });
  }, delay);
}

async function refreshCartUi(cart) {
  var routes = window.themeVariables && window.themeVariables.routes;

  if (routes && routes.cartUrl) {
    try {
      var sectionResponse = await fetch(routes.cartUrl + '?sections=mini-cart', {
        cache: 'reload',
      });
      cart.sections = await sectionResponse.json();
    } catch (error) {
      console.error('[cart-ribbon-sync] sections fetch', error);
    }
  }

  var drawer = getMiniCartDrawer();
  if (drawer) {
    drawer.isRendered = true;
  }

  assignCart(cart);

  try {
    await forceMiniCartRerender();
  } catch (error) {
    console.error('[cart-ribbon-sync] drawer rerender', error);
  }

  scheduleDrawerRefresh(800);

  var detail = { cart: cart, fromRibbonSync: true, replacementDelay: 0 };

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:updated', {
      bubbles: true,
      detail: detail,
    })
  );
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:refresh', {
      detail: detail,
    })
  );
}

var pendingLineRemovalCart = null;
var followUpSyncTimer = null;

async function syncCart(options) {
  options = options || {};

  if (syncing) {
    syncQueued = true;
    if (options.cart) {
      pendingLineRemovalCart = options.cart;
    }
    return;
  }

  syncing = true;

  try {
    var cart = null;

    if (options.cart) {
      cart = await enrichCartItems(
        JSON.parse(JSON.stringify(options.cart))
      );
      pendingLineRemovalCart = null;
    } else if (pendingLineRemovalCart) {
      cart = await enrichCartItems(
        JSON.parse(JSON.stringify(pendingLineRemovalCart))
      );
      pendingLineRemovalCart = null;
    } else {
      cart = await fetchEnrichedCart();
    }

    if (!cart) {
      return;
    }

    if (!cart.items || !cart.items.length) {
      return;
    }

    var cartUpdates = getCartUpdates(cart.items);
    if (!Object.keys(cartUpdates).length) {
      return;
    }

    var updatedCart = await applyCartUpdates(cartUpdates);
    if (!updatedCart) {
      return;
    }

    await refreshCartUi(updatedCart);

    if (
      window.themeVariables &&
      window.themeVariables.settings &&
      window.themeVariables.settings.pageType === 'cart'
    ) {
      window.location.reload();
      return;
    }

    syncQueued = true;
  } catch (error) {
    console.error('[cart-ribbon-sync]', error);
  } finally {
    syncing = false;
    if (syncQueued) {
      syncQueued = false;
      syncCart();
    }
  }
}

function scheduleFollowUpSync() {
  window.clearTimeout(followUpSyncTimer);
  followUpSyncTimer = window.setTimeout(function () {
    syncCart().then(function () {
      scheduleDrawerRefresh(100);
    });
  }, 700);
}

function scheduleSync(event) {
  if (event && event.detail && event.detail.fromRibbonSync) {
    return;
  }

  var delay = 150;
  var isLineRemoval =
    event &&
    event.type === 'line-item-quantity:change:end' &&
    event.detail &&
    event.detail.newLineQuantity === 0;

  if (isLineRemoval) {
    delay = 0;
    pendingLineRemovalCart = event.detail.cart || null;
    scheduleFollowUpSync();
  }

  window.clearTimeout(scheduleSync._timer);
  scheduleSync._timer = window.setTimeout(function () {
    if (isLineRemoval && event.detail && event.detail.cart) {
      syncCart({ cart: event.detail.cart });
      return;
    }
    syncCart();
  }, delay);
}

function watchDrawerOpen() {
  var drawer = getMiniCartDrawer();
  if (!drawer) {
    return;
  }

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.attributeName === 'open' && drawer.hasAttribute('open')) {
        scheduleSync();
        scheduleDrawerRefresh(900);
      }
    });
  });

  observer.observe(drawer, { attributes: true });
}

function boot() {
  document.documentElement.addEventListener('cart:updated', scheduleSync);
  document.documentElement.addEventListener('cart:refresh', scheduleSync);
  document.documentElement.addEventListener('cart:init', scheduleSync);
  document.documentElement.addEventListener('line-item-quantity:change:end', scheduleSync);
  watchDrawerOpen();
  scheduleSync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
